import React, { useEffect } from 'react'
import * as am4core from "@amcharts/amcharts4/core";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import am4geodata_thailandHigh from "@amcharts/amcharts4-geodata/thailandHigh";
import { Slide, Dialog, DialogTitle, DialogContent, DialogContentText, IconButton, TableBody, TableCell, TableHead, TableRow, Paper, Table, TableContainer, TableFooter, TablePagination, Box, useTheme, Typography } from '@mui/material';
import { TransitionProps } from 'react-transition-group/Transition';
import { Close, FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage } from '@mui/icons-material';
import { numberFormatterLocale } from 'view/Backend/IncomeExpenses/Ticket/fnTickit';
import am4geodata_lang_TH from "@amcharts/amcharts4-geodata/lang/TH";

//json
import provinces from "./provinces.json";

/* Chart code */
// Themes begin
am4core.useTheme(am4themes_animated);
// Themes end

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface TablePaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (
        event: React.MouseEvent<HTMLButtonElement>,
        newPage: number,
    ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPage /> : <FirstPage />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPage /> : <LastPage />}
            </IconButton>
        </Box>
    );
}

const MapChart = ({ map, project }) => {

    const [open, setOpen] = React.useState(false);
    const [arrProject, setArrProject] = React.useState([]);
    const [sTitleDialog, setTitleDialog] = React.useState(null);
    const [sBGColor, setBGColor] = React.useState("#fff");
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - arrProject.length) : 0;

    const handleClickOpen = (data) => {
        setTimeout(() => {
            if (data.lstProject) {
                setArrProject([...data.lstProject]);
                setTitleDialog(data.name);
                setBGColor(data.fill);
                setOpen(true);
            }
        }, 1000);
    };

    const handleClose = () => {
        setOpen(false);
        setArrProject([]);
        setTitleDialog(null);
        setBGColor("#fff");
    };

    useEffect(() => {

        let chart = am4core.create("MapChart", am4maps.MapChart);
        chart.geodata = am4geodata_thailandHigh;
        chart.geodataNames = am4geodata_lang_TH;
        chart.language.locale = am4geodata_lang_TH;
        chart.projection = new am4maps.projections.Miller();
        chart.responsive.enabled = true;
        chart.chartContainer.wheelable = true; //enabled zoom in/out with mounth scroll
        chart.seriesContainer.events.disableType("doublehit");

        // var label = chart.chartAndLegendContainer.createChild(am4core.Label);
        // label.text = "จำนวนที่กองทุนจัดการซากดึกดำบรรพ์ สนับสนุน/อุดหนุนในพื้นที่จังหวัดประเทศไทย";
        // label.fontSize = 18;
        // label.align = "center"
        // label.padding(5, 10, 5, 10);
        // label.background.fillOpacity = 0.05;
        // label.background.fill = am4core.color("#000");

        // let titleChart = chart.titles.create();
        // titleChart.text = "จำนวนที่กองทุนจัดการซากดึกดำบรรพ์ สนับสนุน/อุดหนุนในพื้นที่จังหวัดประเทศไทย";
        // titleChart.fontSize = "1.2rem";
        // titleChart.fontWeight = "bold";

        // chart.homeZoomLevel = 5
        // chart.homeGeoPoint = {
        //     latitude: 13.7366,
        //     longitude: 100.4995
        // };

        let polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());
        polygonSeries.useGeodata = true;

        let polygonTemplate = polygonSeries.mapPolygons.template;
        polygonTemplate.tooltipText = "{name}";
        polygonSeries.exclude = ["AQ"];
        polygonSeries.data = map;

        polygonTemplate.adapter.add("tooltipText", function (text, target) {
            let data: any = target.dataItem.dataContext;
            let result = provinces.find(f => f.PROVINCE_CODE === (data.id + "").replace("TH-", ""))?.PROVINCE_NAME ?? text;
            if (data.nBudget) {
                result = "{name}: [bold]{nBudget} บาท"
            }

            return result;
        });

        // polygonSeries.data = [{
        //     "id": "TH-10",
        //     "name": "Bangkok",
        //     "value": 100,
        //     "fill": am4core.color("#F05C5C")
        // }];

        polygonTemplate.propertyFields.fill = "fill";


        let lastSelected;
        polygonTemplate.events.on("hit", function (ev) {
            if (lastSelected) {
                lastSelected.isActive = false;
            }
            ev.target.series.chart.zoomToMapObject(ev.target);
            if (lastSelected !== ev.target) {
                lastSelected = ev.target;
            }

            let dataContext: any = ev.target.dataItem.dataContext;
            handleClickOpen(dataContext);
        })

        // Add image series
        let imageSeries = chart.series.push(new am4maps.MapImageSeries());
        imageSeries.mapImages.template.propertyFields.longitude = "longitude";
        imageSeries.mapImages.template.propertyFields.latitude = "latitude";
        imageSeries.mapImages.template.tooltipText = "{title} : {nBudget} บาท";
        imageSeries.mapImages.template.propertyFields.url = "url";

        let circle = imageSeries.mapImages.template.createChild(am4core.Circle);
        circle.radius = 3;
        circle.propertyFields.fill = "color";
        circle.nonScaling = true;

        let circle2 = imageSeries.mapImages.template.createChild(am4core.Circle);
        circle2.radius = 3;
        circle2.propertyFields.fill = "color";


        circle2.events.on("inited", function (event) {
            animateBullet(event.target);
        })

        function animateBullet(circle) {
            let animation = circle.animate([{ property: "scale", from: 1 / chart.zoomLevel, to: 5 / chart.zoomLevel }, { property: "opacity", from: 1, to: 0 }], 1000, am4core.ease.circleOut);
            animation.events.on("animationended", function (event) {
                animateBullet(event.target.object);
            })
        }

        // let colorSet = new am4core.ColorSet();

        // imageSeries.data = [{
        //     "title": "Project A",
        //     "latitude": 13.7366,
        //     "longitude": 100.4995,
        //     "color": colorSet.next()
        // },
        // {
        //     "title": "Project B",
        //     "latitude": 14.7366,
        //     "longitude": 99.4995,
        //     "color": colorSet.next()
        // }];
        imageSeries.data = project;

        // imageSeries.data.forEach(f => f.color = colorSet.next());

        // Zoom control
        chart.zoomControl = new am4maps.ZoomControl();

        let homeButton = new am4core.Button();
        homeButton.events.on("hit", function () {
            chart.goHome();
        });

        homeButton.icon = new am4core.Sprite();
        homeButton.padding(7, 5, 7, 5);
        homeButton.width = 30;
        homeButton.icon.path = "M16,8 L14,8 L14,16 L10,16 L10,10 L6,10 L6,16 L2,16 L2,8 L0,8 L8,0 L16,8 Z M16,8";
        homeButton.marginBottom = 10;
        homeButton.parent = chart.zoomControl;
        homeButton.insertBefore(chart.zoomControl.plusButton);
    }, [map, project]);

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <div id={"map-title"} style={{ display: "flex", justifyContent: "center", padding: "15px 0" }}>
                <div style={{ padding: "10px 15px", background: "rgb(200 0 255 / 5%)" }}>
                    <Typography fontSize={"1.3rem"} fontWeight={"bold"}>จำนวนโครงการที่กองทุนจัดการซากดึกดำบรรพ์ สนับสนุน/อุดหนุนในพื้นที่จังหวัดในประเทศไทย</Typography>
                </div>
            </div>
            <div id={"MapChart"} style={{ width: "100%", height: "600px" }}></div>
            <Dialog
                open={open}
                onClose={handleClose}
                TransitionComponent={Transition}
                keepMounted
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth={true}
                maxWidth={"lg"}
            >
                <DialogTitle id="alert-dialog-title" sx={{ background: sBGColor, color: "#FFF" }}>
                    {sTitleDialog}
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: "#ffffff"
                        // color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        <TableContainer id='table-pinned-map' component={Paper}>
                            <Table sx={{ maxHeight: 500 }} >
                                <TableHead sx={{ background: "#002351" }}>
                                    <TableRow>
                                        <TableCell align="center" component="th" style={{ width: '60px', color: "#fff" }}>
                                            ลำดับที่
                                        </TableCell>
                                        <TableCell align="center" component="th" style={{ width: '300px', color: "#fff" }}>
                                            โครงการ
                                        </TableCell>
                                        <TableCell align="center" component="th" style={{ width: '250px', color: "#fff" }}>
                                            พื้นที่
                                        </TableCell>
                                        <TableCell align="center" component="th" style={{ width: '150px', color: "#fff" }}>
                                            ตำบล
                                        </TableCell>
                                        <TableCell align="center" component="th" style={{ width: '150px', color: "#fff" }}>
                                            อำเภอ
                                        </TableCell>
                                        <TableCell align="center" component="th" style={{ width: '100px', color: "#fff" }}>
                                            จำนวนเงิน (บาท)
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(rowsPerPage > 0
                                        ? arrProject.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        : arrProject).map((m, i) => {
                                            return (
                                                <TableRow sx={{ backgroundColor: ((i + 1) % 2 === 0) ? "#f2f2f2" : "white" }}>
                                                    <TableCell align="center">{page + i + 1}</TableCell>
                                                    <TableCell>{m.sProjectName}</TableCell>
                                                    <TableCell>{m.sAreaName}</TableCell>
                                                    <TableCell>{m.sDistrictName}</TableCell>
                                                    <TableCell>{m.sSubdistrictName}</TableCell>
                                                    <TableCell align="right">{numberFormatterLocale(m.nBudget, 2)}</TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    {emptyRows > 0 && (
                                        <TableRow style={{ height: 53 * emptyRows }}>
                                            <TableCell colSpan={6}>ไม่พบข้อมูล</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TablePagination
                                            rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                                            colSpan={6}
                                            count={arrProject.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            SelectProps={{
                                                inputProps: {
                                                    'aria-label': 'แถวต่อหน้า',
                                                },
                                                native: true,
                                            }}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            ActionsComponent={TablePaginationActions}
                                        />
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                    </DialogContentText>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default MapChart;